import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { usePermissions } from "../../../hooks/usePermissions";
import { useState } from "react";
import DocumentTemplatesAPI from "../../../api/DocumentTemplates/DocumentTemplates.js";
import {
  EditDocumentTemplateModal,
  CreateDocumentTemplateModal,
  DeleteDocumentTemplateModal,
} from "@/components/Modals";
import { DocumentTemplate, DocumentTemplatesTable } from "@/Features";

const DocumentTemplatesSection = styled(({ className }) => {
  const { currentUser } = usePermissions();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<DocumentTemplate | null>(null);

  const { data, refetch, isLoading } = useQuery({
    queryKey: [
      "document-templates",
      "list",
      { created_by_id: currentUser.user_id, include_shared: true },
    ],
    queryFn: () =>
      DocumentTemplatesAPI.get({
        created_by_id: currentUser.user_id,
        include_shared: true,
      }),
  });

  const handleUpdateTemplate = (values = {}) => {
    DocumentTemplatesAPI.update(values).then(() => refetch());
  };

  const handleAddTemplate = () => {
    setShowAddModal(false);
    refetch();
  };

  const handleEditTemplate = () => {
    setShowEditModal(false);
    refetch();
  };

  const handleDownloadTemplate = (template: DocumentTemplate) => {
    const anchor = document.createElement("a");
    anchor.href = template.download_url;
    anchor.download = template.name;
    anchor.click();

    anchor.remove();
  };

  const templates = data?.data || [];

  return (
    <div className={className}>
      <div>
        These are your document templates. These can be used to create custom
        reports. Documentation on how to use these templates can be found in
        here:{" "}
        <a
          href="https://docs.monolithforensics.com/monolith/monolith-features/case-reports/report-templates"
          target="_blank"
          rel="noreferrer"
        >
          Document Templates
        </a>
      </div>
      {showDeleteModal && (
        <DeleteDocumentTemplateModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onCancel={() => setShowDeleteModal(false)}
          onSubmit={() => refetch()}
          defaultInfo={selectedTemplate}
        />
      )}
      {showAddModal && (
        <CreateDocumentTemplateModal
          show={showAddModal}
          onClose={() => setShowAddModal(false)}
          onCancel={() => setShowAddModal(false)}
          onSubmit={handleAddTemplate}
        />
      )}
      {showEditModal && (
        <EditDocumentTemplateModal
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          onCancel={() => setShowEditModal(false)}
          onSubmit={handleEditTemplate}
          defaultInfo={selectedTemplate}
        />
      )}
      <DocumentTemplatesTable
        data={templates}
        onEdit={(e) => {
          setSelectedTemplate(e);
          setShowEditModal(true);
        }}
        onDelete={(e) => {
          setSelectedTemplate(e);
          setShowDeleteModal(true);
        }}
        onUpdate={handleUpdateTemplate}
        onAdd={() => setShowAddModal(true)}
        onDownload={handleDownloadTemplate}
        loading={isLoading}
      />
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  overflow-y: auto;

  a {
    color: ${({ theme }) => theme.palette.primary.main};

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default DocumentTemplatesSection;
